/**
 * Get Current User API Contract
 *
 * GET /v1/users/me
 * Retrieve the authenticated user's profile, role flags, and school
 * memberships. The shape differs from `GET /v1/users/{id}` — this
 * endpoint includes per-school role flags and membership status.
 *
 * Source of truth: `apps/backend/src/api/users/(app-users)/v1/use-cases/me/schema.ts`
 * Mobile consumer: `apps/mobile/src/context/hooks/use-me.ts`,
 * `apps/mobile/src/services/session/session-lifecycle.ts`
 */

import { z } from 'zod'
import { ProvisioningIssueSchema } from '../../schemas/entities/provisioning-issue'

/**
 * Per-school role flags returned alongside each membership.
 */
export const MeSchoolUserRolesSchema = z.object({
  isStudent: z.boolean(),
  isParent: z.boolean(),
  isFaculty: z.boolean(),
  isAdmin: z.boolean(),
  isStaff: z.boolean(),
  isCandidate: z.boolean().optional(),
  isBetaTester: z.boolean()
})

export type MeSchoolUserRoles = z.infer<typeof MeSchoolUserRolesSchema>

/**
 * One school the authenticated user belongs to, with membership and role
 * details.
 */
export const MeSchoolUserSchema = z.object({
  id: z.string().describe('School user public ID (CUID)'),
  status: z.string().nullable().describe('Membership status (ACTIVE, PENDING, etc.)'),
  // Parked-state marker (see entities/provisioning-issue). Present when this
  // membership is parked on a deterministic provisioning failure (BB SKY 403);
  // omitted/null otherwise. The mobile app keys its "connection issue" screen on
  // this marker, not on `status`.
  provisioningIssue: ProvisioningIssueSchema.nullable().optional(),
  roles: MeSchoolUserRolesSchema,
  // School permission keys this membership grants (never a super-admin
  // override). Plain strings, not the key enum, so a key added on the backend
  // cannot fail an older client's parse. Omitted when the backend could not
  // resolve them; an empty array means "resolved, holds none".
  permissions: z.array(z.string()).optional(),
  school: z
    .object({
      id: z.string().describe('School unique identifier (CUID)'),
      name: z.string(),
      slug: z.string().optional(),
      environmentName: z.string().nullable().optional()
    })
    .nullable()
    .optional()
})

export type MeSchoolUser = z.infer<typeof MeSchoolUserSchema>

/**
 * The authenticated user's profile.
 */
export const MeUserSchema = z.object({
  id: z.string().describe('User unique identifier (CUID)'),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  photoUrlThumbnail: z.string().nullable().optional(),
  photoUrlLarge: z.string().nullable().optional(),
  schoolUsers: z.array(MeSchoolUserSchema)
})

export type MeUser = z.infer<typeof MeUserSchema>

/**
 * Response envelope for `GET /v1/users/me`.
 */
export const MeResponseSchema = z.object({
  data: MeUserSchema,
  meta: z.object({}).optional(),
  message: z.string()
})

export type MeResponse = z.infer<typeof MeResponseSchema>
